import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    Button,
    Fab,
    Collapse,
    IconButton,
    Paper,
    Drawer,
    ListItemButton,
    ListItemText,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Add,
    ArrowBack,
    ExpandMore,
    ExpandLess,
    ShoppingCart as CartIcon,
    AddCircleOutline,
    RemoveCircleOutline,
    LocationOn as LocationOnIcon,
    ChevronRight as ChevronRightIcon,
    CheckCircle as CheckCircleIcon,
    LocalShipping,
    Store as StorePickupIcon,
    CalendarMonth as SubscriptionIcon,
} from '@mui/icons-material';
import { businessAPI, orderAPI, addressAPI } from '../../api';
import { useCategoryScrollspy, StickyCategoryBar, sectionScrollMargin } from '../ProductCategoryNav';
import CartReviewSheet from '../CartReviewSheet';
import ProductDetailSheet from '../ProductDetailSheet';
import ProductImage from '../common/ProductImage';
import { groupByCategory } from '../../utils/groupByCategory';

// Local-date YYYY-MM-DD for tomorrow (built from the local clock, not toISOString, which
// shifts to UTC and can read a day early before 05:30 IST).
const planTomorrowISO = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const STATUS_COLORS = {
    created: 'info',
    accepted: 'primary',
    delivered: 'success',
    cancelled: 'error',
};
const PAYMENT_COLORS = {
    due: 'error',
    paid_directly: 'success',
    pay_later: 'warning',
    invoice: 'info',
    void: 'default',
};

const fmt = (n) => parseFloat(n || 0).toFixed(2);

// ─── Order History ───────────────────────────────────────────────────────────

const OrderCard = ({ order }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent
                sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer' }}
                onClick={() => setExpanded(p => !p)}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            ₹{fmt(order.f_total_amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {order.f_inserted_at
                                ? new Date(order.f_inserted_at).toLocaleDateString('en-IN')
                                : '—'}
                            {order.f_delivery_date && (
                                <> · Delivery: {new Date(order.f_delivery_date).toLocaleDateString('en-IN')}</>
                            )}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Chip label={order.f_status} size="small"
                            color={STATUS_COLORS[order.f_status] || 'default'}
                            sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip label={order.f_payment_status?.replace('_', ' ')} size="small"
                            color={PAYMENT_COLORS[order.f_payment_status] || 'default'}
                            variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                </Box>

                <Collapse in={expanded}>
                    <Divider sx={{ my: 1 }} />
                    {order.items?.map((item) => (
                        <Box key={item.f_id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">
                                {item.product_name} × {item.f_quantity}
                            </Typography>
                            <Typography variant="caption" fontWeight="medium">
                                ₹{fmt(item.f_line_total)}
                            </Typography>
                        </Box>
                    ))}
                    {order.f_notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Note: {order.f_notes}
                        </Typography>
                    )}
                </Collapse>
            </CardContent>
        </Card>
    );
};

// ─── New Order (product grid + cart) ─────────────────────────────────────────

// Cart is persisted locally so it survives navigation/refresh, but only for one
// vendor at a time — opening a different vendor starts a fresh (empty) cart.
const CART_KEY = 'vendora.customerCart';

// Default note shown while ordering when the vendor hasn't set their own Store Note.
const DEFAULT_ORDER_NOTE = 'Prices are indicative and may vary. Your order may be revised by the vendor before confirmation.';

export const NewOrderView = ({ businessId, user, onBack, onOrderCreated, hideHeader = false, startMode = 'order' }) => {
    const [products, setProducts] = useState([]);
    const [categoryOrder, setCategoryOrder] = useState([]);  // vendor's saved category order
    const [cart, setCart] = useState({});   // { productId: { product, quantity } }
    const cartHydrated = useRef(false);      // guards persistence until restore runs
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [deliveryAddress, setDeliveryAddress] = useState(null);
    const [addrDrawerOpen, setAddrDrawerOpen] = useState(false);
    const [detailProduct, setDetailProduct] = useState(null);
    const [cartReviewOpen, setCartReviewOpen] = useState(false);
    const [orderNotes, setOrderNotes] = useState('');
    const [manualAddress, setManualAddress] = useState('');
    const [placedOrder, setPlacedOrder] = useState(null); // confirmation after placing
    // Plans (subscriptions) — same pick -> review -> confirm shape as ordering. The vendor
    // already has this customer's details, so plan checkout only needs a start date + note.
    const [planProducts, setPlanProducts] = useState([]);
    const [planCart, setPlanCart] = useState({});          // { productId: qty }
    const [planStep, setPlanStep] = useState(null);        // null | 'browse' | 'checkout' | 'confirmed'
    const [planStartDate, setPlanStartDate] = useState(planTomorrowISO());
    const [planNote, setPlanNote] = useState('');
    const [planError, setPlanError] = useState('');
    const [planSubmitting, setPlanSubmitting] = useState(false);
    const [storeMode, setStoreMode] = useState(startMode); // 'order' | 'subscription'
    const [deliveryCharge, setDeliveryCharge] = useState(0);
    const [fulfillmentMode, setFulfillmentMode] = useState('delivery');  // what the business offers: delivery|pickup|both
    const [fulfillmentType, setFulfillmentType] = useState('delivery');  // customer's choice; null = must choose (both)
    const [storeNote, setStoreNote] = useState('');  // vendor's store note, shown while ordering

    useEffect(() => {
        // Plans this customer can still sign up for (excludes ones they already have).
        businessAPI.getMyPlanProducts(businessId)
            .then(r => setPlanProducts(r.data || []))
            .catch(() => setPlanProducts([]));
    }, [businessId]);

    useEffect(() => {
        cartHydrated.current = false; // pause persistence until this vendor's cart is restored
        const load = async () => {
            try {
                const res = await businessAPI.getMyProducts(businessId);
                const prods = res.data || [];
                setProducts(prods);
                // Restore a saved cart only if it belongs to THIS vendor; otherwise
                // start fresh and drop the other vendor's cart.
                try {
                    const saved = JSON.parse(localStorage.getItem(CART_KEY) || 'null');
                    if (saved && saved.businessId === businessId && saved.items) {
                        const restored = {};
                        prods.forEach(p => {
                            const qty = saved.items[p.f_id];
                            if (qty > 0) restored[p.f_id] = { product: p, quantity: qty };
                        });
                        setCart(restored);
                    } else {
                        setCart({});
                        localStorage.removeItem(CART_KEY);
                    }
                } catch { setCart({}); localStorage.removeItem(CART_KEY); }
            } catch (e) {
                setError(e?.response?.data?.detail || 'Failed to load products');
            } finally {
                cartHydrated.current = true;
                setLoadingProducts(false);
            }
        };
        load();
    }, [businessId]);

    // Vendor's saved category display order (best-effort; falls back to first-appearance order)
    useEffect(() => {
        businessAPI.getCategoryOrder(businessId)
            .then(res => setCategoryOrder(res.data?.ordered_names || []))
            .catch(() => setCategoryOrder([]));
    }, [businessId]);

    // Persist the cart (quantities keyed by product) for this vendor. Fresh product
    // objects are re-attached on restore, so stored prices never go stale.
    useEffect(() => {
        if (!cartHydrated.current) return;
        const items = {};
        Object.values(cart).forEach(({ product, quantity }) => { items[product.f_id] = quantity; });
        if (Object.keys(items).length > 0) {
            localStorage.setItem(CART_KEY, JSON.stringify({ businessId, items }));
        } else {
            localStorage.removeItem(CART_KEY);
        }
    }, [cart, businessId]);

    // Applicable delivery charge + what fulfillment the business offers.
    useEffect(() => {
        businessAPI.getMyBusinessProfile(businessId)
            .then(res => {
                setDeliveryCharge(parseFloat(res.data?.delivery_charge) || 0);
                const mode = res.data?.f_fulfillment_mode || 'delivery';
                setFulfillmentMode(mode);
                // Single-mode businesses auto-select; 'both' forces the customer to choose.
                setFulfillmentType(mode === 'both' ? null : mode);
                setStoreNote(res.data?.f_online_store_notes || '');
            })
            .catch(() => setDeliveryCharge(0));
    }, [businessId]);

    useEffect(() => {
        addressAPI.list().then(res => {
            const addrs = res.data || [];
            setSavedAddresses(addrs);
            setDeliveryAddress(addrs.find(a => a.f_default) || addrs[0] || null);
        }).catch(() => {});
    }, []);

    const updateCart = (product, delta) => {
        const min = parseFloat(product.f_min_order_qty) || 1;
        setCart(prev => {
            const current = prev[product.f_id]?.quantity || 0;
            const next = current + delta;
            // Below the minimum (incl. 0) → drop from cart, matching the public store.
            if (next < min) {
                const { [product.f_id]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [product.f_id]: { product, quantity: next } };
        });
    };

    const cartItems = Object.values(cart);
    const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);
    const cartTotal = cartItems.reduce((s, i) => {
        const price = parseFloat(i.product.current_price || 0);
        const disc = parseFloat(i.product.current_discount || 0);
        return s + i.quantity * Math.max(0, price - disc);
    }, 0);

    const isPickup = fulfillmentType === 'pickup';
    const effectiveDelivery = isPickup ? 0 : deliveryCharge;

    const handleSubmit = async () => {
        if (cartItems.length === 0) return;
        // Force a choice when the business offers both.
        if (fulfillmentMode === 'both' && !fulfillmentType) {
            setError('Please choose Delivery or Store Pickup');
            setCartReviewOpen(true);
            return;
        }
        // Delivery orders with saved addresses need one selected.
        if (!isPickup && savedAddresses.length > 0 && !deliveryAddress) {
            setError('Please select a delivery address');
            setCartReviewOpen(true);
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            // Check that all cart items are still in the vendor's catalog
            try {
                const { data: freshProds } = await businessAPI.getMyProducts(businessId);
                const freshIds = new Set((freshProds || []).map(p => p.f_id));
                const gone = cartItems.filter(i => !freshIds.has(i.product.f_id));
                if (gone.length > 0) {
                    setCart(prev => {
                        const next = { ...prev };
                        gone.forEach(i => delete next[i.product.f_id]);
                        return next;
                    });
                    setError(`No longer available — removed from cart: ${gone.map(i => i.product.f_name).join(', ')}`);
                    setCartReviewOpen(true);
                    return;
                }
            } catch { /* network error — server will validate */ }

            const addrNote = !isPickup
                ? (deliveryAddress?.f_unit
                    ? `Deliver to: ${deliveryAddress.f_unit}${deliveryAddress.community_name ? ', ' + deliveryAddress.community_name : ''}`
                    : manualAddress.trim() ? `Deliver to: ${manualAddress.trim()}` : null)
                : null;
            const notes = [orderNotes.trim() || null, addrNote].filter(Boolean).join('\n') || null;
            const res = await orderAPI.createCustomerOrder(businessId, {
                f_walkin_name: user?.f_name || user?.f_mobile,
                f_fulfillment_type: fulfillmentType,
                items: cartItems.map(i => ({
                    f_business_product_id: i.product.f_id,
                    f_quantity: i.quantity,
                    f_unit_price: parseFloat(i.product.current_price || 0),
                    f_discount: parseFloat(i.product.current_discount || 0),
                })),
                f_notes: notes,
            });
            setCart({});
            setOrderNotes('');
            setManualAddress('');
            setCartReviewOpen(false);
            setPlacedOrder(res.data); // show confirmation instead of silently leaving
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to place order');
        } finally {
            setSubmitting(false);
        }
    };

    // Group products by category. A product can be in several categories (f_categories);
    // it shows under each. Category order follows the vendor's saved order.
    const { grouped, categories } = useMemo(
        () => groupByCategory(products, 'f_categories', 'f_business_product_category', categoryOrder),
        [products, categoryOrder]
    );

    const { selectedCategory, scrollToCategory, stickyBarRef, setSectionRef, setChipRef } =
        useCategoryScrollspy(categories);
    // Plans get the same category grouping + scrollspy so the plan browser looks like the shop.
    const { grouped: planGrouped, categories: planCategories } = useMemo(
        () => groupByCategory(planProducts, 'f_categories', 'f_business_product_category', categoryOrder),
        [planProducts, categoryOrder]
    );
    const planSpy = useCategoryScrollspy(planCategories);

    // Sticky bar pins below the fixed dashboard app bar (48px + safe area)
    const STICKY_TOP = 'calc(48px + env(safe-area-inset-top, 0px))';

    // ── Plans ────────────────────────────────────────────────────────────────
    const planItems = Object.entries(planCart)
        .filter(([, q]) => q > 0)
        .map(([pid, q]) => ({ f_business_product_id: Number(pid), f_quantity: q }));
    const planCount = planItems.length;

    const setPlanQty = (productId, delta) => setPlanCart(prev => {
        const next = Math.max(0, (prev[productId] || 0) + delta);
        const copy = { ...prev };
        if (next === 0) delete copy[productId]; else copy[productId] = next;
        return copy;
    });

    // The whole cart is one all-or-nothing request the vendor approves, sharing one start date.
    const submitPlanSignup = async () => {
        setPlanError('');
        if (planCount === 0) { setPlanError('Please choose at least one plan.'); return; }
        if (!planStartDate || planStartDate < planTomorrowISO()) {
            setPlanError('Please choose tomorrow or a later start date.'); return;
        }
        setPlanSubmitting(true);
        try {
            await businessAPI.requestPlanSignup(businessId, {
                items: planItems,
                f_effective_from_date: planStartDate,
                f_note: planNote.trim() || null,
            });
            setPlanStep('confirmed');
        } catch (e) {
            setPlanError(e?.response?.data?.detail || 'Could not send your request. Please try again.');
        } finally {
            setPlanSubmitting(false);
        }
    };

    if (loadingProducts) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    // ── Plan: checkout ───────────────────────────────────────────────────────
    if (planStep === 'checkout') {
        return (
            <Box sx={{ pb: 10 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={() => setPlanStep(null)} size="small"><ArrowBack /></IconButton>
                    <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>Your Subscription</Typography>
                </Box>

                {planError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPlanError('')}>{planError}</Alert>}

                <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {planItems.map(it => {
                            const p = planProducts.find(x => x.f_id === it.f_business_product_id);
                            return (
                                <Box key={it.f_business_product_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                                    <Typography variant="body2">{p?.f_name || 'Plan'} × {it.f_quantity}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ₹{Number((p?.current_price || 0) * it.f_quantity).toLocaleString('en-IN')}
                                    </Typography>
                                </Box>
                            );
                        })}
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                            Billed as delivered — nothing is charged now.
                        </Typography>
                    </CardContent>
                </Card>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Start from</Typography>
                <TextField type="date" fullWidth size="small" value={planStartDate}
                    onChange={e => setPlanStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} inputProps={{ min: planTomorrowISO() }}
                    helperText="Earliest is tomorrow — your vendor confirms it"
                    sx={{ mb: 2 }} />

                <TextField label="Note (optional)" fullWidth size="small" value={planNote}
                    onChange={e => setPlanNote(e.target.value)} placeholder="e.g. deliver in the morning" />

                <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 720, mx: 'auto',
                    p: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', borderRadius: '16px 16px 0 0', zIndex: 1200 }}>
                    <Button fullWidth variant="contained" size="large" onClick={submitPlanSignup} disabled={planSubmitting}
                        sx={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }}>
                        {planSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Send request'}
                    </Button>
                </Paper>
            </Box>
        );
    }

    // ── Plan: confirmed ──────────────────────────────────────────────────────
    if (planStep === 'confirmed') {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" fontWeight={800}>Request sent!</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                    Your vendor will confirm your subscription{planCount > 1 ? 's' : ''} and the start date.
                </Typography>
                <Button variant="contained" onClick={() => { setPlanCart({}); setPlanNote(''); setPlanStep(null); onBack && onBack(); }}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Done
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: (cartCount > 0 || planCount > 0) ? 10 : 2 }}>
            {!hideHeader && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={onBack} size="small">
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>
                        New Order
                    </Typography>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Store note — the vendor's own note, else a default note (order mode) */}
            {(() => {
                const note = storeNote || (storeMode === 'order' ? DEFAULT_ORDER_NOTE : '');
                if (!note) return null;
                return (
                    <Box sx={{ bgcolor: '#fff8e1', border: '1px solid', borderColor: '#ffe082', borderRadius: 1.5, px: 1.5, py: 1.25, mb: 1.5 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#f57c00', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.25 }}>
                            Please note
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5d4037', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {note}
                        </Typography>
                    </Box>
                );
            })()}

            {/* Order / Subscription toggle — only when business has plan products */}
            {planProducts.length > 0 && (
                <Box sx={{ display: 'flex', bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 2.5, p: 0.4, gap: 0.4, mb: 1.5 }}>
                    {[
                        { val: 'order', icon: <CartIcon sx={{ fontSize: 17 }} />, label: 'One-time Order' },
                        { val: 'subscription', icon: <SubscriptionIcon sx={{ fontSize: 17 }} />, label: 'Subscription Plan' },
                    ].map(({ val, icon, label }) => (
                        <Button key={val} fullWidth
                            onClick={() => setStoreMode(val)}
                            startIcon={icon}
                            sx={{
                                textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8,
                                fontSize: '0.85rem',
                                ...(storeMode === val
                                    ? { bgcolor: 'background.paper', boxShadow: '0 1px 4px rgba(0,0,0,0.14)', color: 'primary.main', '&:hover': { bgcolor: 'background.paper' } }
                                    : { color: 'text.secondary', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }),
                            }}
                        >
                            {label}
                        </Button>
                    ))}
                </Box>
            )}

            {/* Delivery address bar — order mode only */}
            {storeMode === 'order' && savedAddresses.length > 0 && (
                <Box
                    onClick={() => setAddrDrawerOpen(true)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1.25,
                        border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5,
                        cursor: 'pointer', bgcolor: 'primary.lighter' }}>
                    <LocationOnIcon fontSize="small" color="primary" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>Delivering to</Typography>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                            {deliveryAddress ? deliveryAddress.f_unit : 'Select address'}
                        </Typography>
                        {deliveryAddress?.community_name && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {deliveryAddress.community_name}
                            </Typography>
                        )}
                    </Box>
                    <ChevronRightIcon fontSize="small" color="primary" />
                </Box>
            )}

            {/* Address picker drawer */}
            <Drawer anchor="bottom" open={storeMode === 'order' && addrDrawerOpen} onClose={() => setAddrDrawerOpen(false)}
                sx={{ zIndex: 1400 }}
                PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Select delivery address</Typography>
                {savedAddresses.map(a => (
                    <ListItemButton key={a.f_id} selected={deliveryAddress?.f_id === a.f_id}
                        onClick={() => { setDeliveryAddress(a); setAddrDrawerOpen(false); }}
                        sx={{ borderRadius: 1, mb: 0.5 }}>
                        <LocationOnIcon sx={{ mr: 1.5, fontSize: 20 }} color="action" />
                        <ListItemText
                            primary={`${a.f_label} · ${a.f_unit}`}
                            secondary={a.community_name || ''}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }} />
                    </ListItemButton>
                ))}
            </Drawer>

            {/* Order mode: categories, products, cart bar */}
            {storeMode === 'order' && (
                <>
                    <StickyCategoryBar
                        categories={categories}
                        selectedCategory={selectedCategory}
                        onSelect={scrollToCategory}
                        stickyBarRef={stickyBarRef}
                        setChipRef={setChipRef}
                        top={STICKY_TOP}
                        sx={{ mx: { xs: -1.5, sm: -3 }, px: { xs: 1.5, sm: 3 } }}
                    />

                    {products.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No products available.
                        </Typography>
                    ) : (
                        categories.map((cat) => (
                            <Box key={cat} ref={setSectionRef(cat)} data-cat={cat}
                                sx={{ mb: 2, scrollMarginTop: sectionScrollMargin(STICKY_TOP) }}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {cat}
                                </Typography>
                                {grouped[cat].map((p) => {
                                    const qty    = cart[p.f_id]?.quantity || 0;
                                    const price  = parseFloat(p.current_price || 0);
                                    const disc   = parseFloat(p.current_discount || 0);
                                    const minQty = parseFloat(p.f_min_order_qty) || 1;
                                    const step   = parseFloat(p.f_increment_order_qty) || 1;
                                    return (
                                        <Card key={p.f_id} variant="outlined" sx={{ mt: 1 }}>
                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                    <Box onClick={() => setDetailProduct(p)}
                                                        sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}>
                                                        <ProductImage src={p.f_image_url} name={p.f_name} fontSize="1.1rem" />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight="medium" noWrap>{p.f_name}</Typography>
                                                        {disc > 0 ? (
                                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>₹{fmt(price)}</Typography>
                                                                <Typography variant="caption" color="success.main" fontWeight="bold">₹{fmt(price - disc)}</Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">₹{fmt(price)}</Typography>
                                                        )}
                                                        {minQty > 1 && qty === 0 && (
                                                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                                                Min {minQty}{step !== 1 ? ` · Step ${step}` : ''}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flexShrink: 0 }}>
                                                        {qty === 0 ? (
                                                            <Button variant="outlined" size="small" onClick={() => updateCart(p, minQty)}
                                                                sx={{ minWidth: 64, height: 32, fontWeight: 700, borderRadius: 1.5, textTransform: 'none' }}>
                                                                Add
                                                            </Button>
                                                        ) : (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <IconButton size="small" onClick={() => updateCart(p, -step)}>
                                                                    <RemoveCircleOutline fontSize="small" color="primary" />
                                                                </IconButton>
                                                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 24, textAlign: 'center' }}>{qty}</Typography>
                                                                <IconButton size="small" onClick={() => updateCart(p, step)}>
                                                                    <AddCircleOutline fontSize="small" color="primary" />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        ))
                    )}

                    {cartCount > 0 && (
                        <Paper elevation={4} sx={{
                            position: 'fixed',
                            bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
                            left: 0, right: 0, p: 1.5,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                            zIndex: 1000,
                        }}>
                            <Box onClick={() => setCartReviewOpen(true)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                                <CartIcon color="primary" />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                        {cartCount} item{cartCount !== 1 ? 's' : ''} · ₹{cartTotal.toFixed(2)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                                        <Typography variant="caption" fontWeight="medium">View cart</Typography>
                                        <ExpandLess sx={{ fontSize: 16 }} />
                                    </Box>
                                </Box>
                            </Box>
                            <Button variant="contained" size="small" onClick={() => setCartReviewOpen(true)} sx={{ flexShrink: 0 }}>
                                Review Order
                            </Button>
                        </Paper>
                    )}
                </>
            )}

            {/* Subscription mode: plan product catalog + continue bar */}
            {storeMode === 'subscription' && (
                <>
                    {planCategories.length > 1 && (
                        <StickyCategoryBar
                            categories={planCategories}
                            selectedCategory={planSpy.selectedCategory}
                            onSelect={planSpy.scrollToCategory}
                            stickyBarRef={planSpy.stickyBarRef}
                            setChipRef={planSpy.setChipRef}
                            top={STICKY_TOP}
                            sx={{ mx: { xs: -1.5, sm: -3 }, px: { xs: 1.5, sm: 3 }, mb: 1 }}
                        />
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Pick what you want delivered regularly. Your vendor will confirm before it starts.
                    </Typography>

                    {planProducts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No subscriptions available to add right now.
                        </Typography>
                    ) : planCategories.map(cat => (
                        <Box key={cat} ref={planSpy.setSectionRef(cat)} data-cat={cat}
                            sx={{ mb: 2, scrollMarginTop: sectionScrollMargin(STICKY_TOP) }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {cat}
                            </Typography>
                            {planGrouped[cat].map(p => {
                                const qty   = planCart[p.f_id] || 0;
                                const price = parseFloat(p.current_price || 0);
                                const disc  = parseFloat(p.current_discount || 0);
                                const taken = !!p.already_planned;
                                return (
                                    <Card key={p.f_id} variant="outlined"
                                        sx={{ mt: 1, ...(taken && { bgcolor: 'action.hover', borderStyle: 'dashed' }) }}>
                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, ...(taken && { opacity: 0.55 }) }}>
                                                <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                                    <ProductImage src={p.f_image_url} name={p.f_name} fontSize="1.1rem" />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight="medium" noWrap>{p.f_name}</Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                        {disc > 0 && (
                                                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>₹{fmt(price)}</Typography>
                                                        )}
                                                        <Typography variant="caption" color={disc > 0 ? 'success.main' : 'text.secondary'} fontWeight={disc > 0 ? 'bold' : 'regular'}>
                                                            ₹{fmt(price - disc)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.disabled">
                                                            {p.f_billing_type === 'MonthlyFlatFee' ? '/ month' : '/ delivery'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ flexShrink: 0 }}>
                                                    {taken ? (
                                                        <Chip label="Already added" size="small"
                                                            sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700 }} />
                                                    ) : qty === 0 ? (
                                                        <Button variant="outlined" size="small" onClick={() => setPlanQty(p.f_id, 1)}
                                                            sx={{ minWidth: 64, height: 32, fontWeight: 700, borderRadius: 1.5, textTransform: 'none' }}>
                                                            Add
                                                        </Button>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <IconButton size="small" onClick={() => setPlanQty(p.f_id, -1)}>
                                                                <RemoveCircleOutline fontSize="small" color="primary" />
                                                            </IconButton>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 20, textAlign: 'center' }}>{qty}</Typography>
                                                            <IconButton size="small" onClick={() => setPlanQty(p.f_id, 1)}>
                                                                <AddCircleOutline fontSize="small" color="primary" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                            {taken && (
                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                                    You already have this subscription — change it from Home → Subscriptions.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    ))}

                    {planCount > 0 && (
                        <Paper elevation={8} sx={{
                            position: 'fixed',
                            bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
                            left: 0, right: 0, maxWidth: 720, mx: 'auto',
                            p: 1.5, borderRadius: '16px 16px 0 0', zIndex: 1200,
                        }}>
                            <Button fullWidth variant="contained" size="large"
                                onClick={() => { setPlanError(''); setPlanStep('checkout'); }}
                                sx={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }}>
                                Continue · {planCount} subscription{planCount > 1 ? 's' : ''}
                            </Button>
                        </Paper>
                    )}
                </>
            )}

            {/* Cart review sheet */}
            <CartReviewSheet
                open={cartReviewOpen}
                onClose={() => setCartReviewOpen(false)}
                items={cartItems.map(({ product: p, quantity }) => {
                    const price = parseFloat(p.current_price || 0);
                    const disc  = parseFloat(p.current_discount || 0);
                    return {
                        id: p.f_id,
                        name: p.f_name,
                        imageUrl: p.f_image_url,
                        unitPrice: Math.max(0, price - disc),
                        quantity,
                        raw: p,
                    };
                })}
                onInc={(it) => updateCart(it.raw, parseFloat(it.raw.f_increment_order_qty) || 1)}
                onDec={(it) => updateCart(it.raw, -(parseFloat(it.raw.f_increment_order_qty) || 1))}
                total={cartTotal}
                deliveryCharge={effectiveDelivery}
                primaryLabel={`Place Order · ₹${(cartTotal + effectiveDelivery).toFixed(2)}`}
                onPrimary={handleSubmit}
                busy={submitting}
            >
                {/* Delivery / Store Pickup — only when the business offers both */}
                {fulfillmentMode === 'both' && (
                    <Box sx={{ mb: 1.5 }}>
                        <ToggleButtonGroup
                            value={fulfillmentType}
                            exclusive
                            fullWidth
                            size="small"
                            onChange={(_, val) => { if (val) setFulfillmentType(val); }}
                        >
                            <ToggleButton value="delivery" sx={{ textTransform: 'none', fontWeight: 600, gap: 0.75 }}>
                                <LocalShipping sx={{ fontSize: 18 }} /> Delivery
                            </ToggleButton>
                            <ToggleButton value="pickup" sx={{ textTransform: 'none', fontWeight: 600, gap: 0.75 }}>
                                <StorePickupIcon sx={{ fontSize: 18 }} /> Store Pickup
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {!fulfillmentType && (
                            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                Please choose Delivery or Store Pickup.
                            </Typography>
                        )}
                    </Box>
                )}
                {/* Address — shown for both; required only for delivery, optional for pickup */}
                {savedAddresses.length === 0 && !isPickup ? (
                    <TextField
                        fullWidth size="small"
                        label="Door / Flat number (optional)"
                        placeholder="e.g. Flat 4B, Block C"
                        value={manualAddress}
                        onChange={e => setManualAddress(e.target.value)}
                        InputProps={{ startAdornment: <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} /> }}
                        sx={{ mb: 1.5 }}
                    />
                ) : deliveryAddress ? (
                    <Box
                        onClick={() => { setCartReviewOpen(false); setAddrDrawerOpen(true); }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1, borderRadius: 1.5,
                            bgcolor: 'action.hover', cursor: savedAddresses.length > 1 ? 'pointer' : 'default' }}>
                        <LocationOnIcon fontSize="small" color="primary" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Delivering to</Typography>
                            <Typography variant="body2" fontWeight="medium" noWrap>
                                {deliveryAddress.f_unit}{deliveryAddress.community_name ? `, ${deliveryAddress.community_name}` : ''}
                            </Typography>
                        </Box>
                        {savedAddresses.length > 1 && <ChevronRightIcon fontSize="small" color="action" />}
                    </Box>
                ) : (
                    <Box
                        onClick={() => { setCartReviewOpen(false); setAddrDrawerOpen(true); }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1, borderRadius: 1.5,
                            border: '1px solid', borderColor: isPickup ? 'divider' : 'error.main', cursor: 'pointer' }}>
                        <LocationOnIcon fontSize="small" color={isPickup ? 'action' : 'error'} />
                        <Typography variant="body2" color={isPickup ? 'text.secondary' : 'error.main'} fontWeight="medium">
                            {isPickup ? 'Add an address (optional)' : 'Select a delivery address'}
                        </Typography>
                    </Box>
                )}
                <TextField
                    fullWidth size="small" multiline minRows={2}
                    label="Note for the vendor (optional)"
                    placeholder="e.g. preferred delivery time, landmark, special instructions"
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    sx={{ mb: 1.5 }}
                />
            </CartReviewSheet>

            {/* Product detail sheet (tap image to enlarge) */}
            <ProductDetailSheet
                product={detailProduct && {
                    name: detailProduct.f_name,
                    imageUrl: detailProduct.f_image_url,
                    unitPrice: Math.max(0, (parseFloat(detailProduct.current_price) || 0) - (parseFloat(detailProduct.current_discount) || 0)),
                    originalPrice: parseFloat(detailProduct.current_price) || 0,
                    description: detailProduct.f_description || null,
                }}
                quantity={detailProduct ? (cart[detailProduct.f_id]?.quantity || 0) : 0}
                onAdd={() => updateCart(detailProduct, parseFloat(detailProduct.f_min_order_qty) || 1)}
                onInc={() => updateCart(detailProduct, parseFloat(detailProduct.f_increment_order_qty) || 1)}
                onDec={() => updateCart(detailProduct, -(parseFloat(detailProduct.f_increment_order_qty) || 1))}
                onClose={() => setDetailProduct(null)}
            />

            {/* Order placed confirmation */}
            <Drawer anchor="bottom" open={!!placedOrder}
                onClose={() => { setPlacedOrder(null); onOrderCreated(); }}
                PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 3, textAlign: 'center', pb: 'calc(24px + env(safe-area-inset-bottom, 0px))' } }}>
                {placedOrder && (
                    <>
                        <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                        <Typography variant="h6" fontWeight={800} gutterBottom>Order Placed!</Typography>
                        <Typography variant="body1" color="text.secondary">
                            Order <strong>#{placedOrder.f_order_number ?? placedOrder.f_id}</strong> · <strong>₹{parseFloat(placedOrder.f_total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2.5 }}>
                            The vendor will confirm your order shortly.
                        </Typography>
                        <Button fullWidth variant="contained" size="large"
                            onClick={() => { setPlacedOrder(null); onOrderCreated(); }}
                            sx={{ height: 48, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                            Done
                        </Button>
                    </>
                )}
            </Drawer>
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CustomerOrdersPage = ({ businessId, user, startWithNewOrder = false, onNewOrderBack }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState('');
    const [showNewOrder, setShowNewOrder] = useState(startWithNewOrder);
    const [successMsg, setSuccessMsg] = useState('');

    const LIMIT = 20;

    const loadOrders = useCallback(async (pageNum = 1, append = false) => {
        if (pageNum === 1) setLoading(true); else setLoadingMore(true);
        try {
            const res = await businessAPI.getMyOrders(businessId, { page: pageNum, limit: LIMIT });
            const data = res.data || [];
            setOrders(prev => append ? [...prev, ...data] : data);
            setHasMore(data.length === LIMIT);
            setPage(pageNum);
        } catch {
            setError('Failed to load orders');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [businessId]);

    useEffect(() => {
        if (!businessId) return;
        loadOrders(1);
    }, [businessId, loadOrders]);

    if (!businessId) return null;

    if (showNewOrder) {
        return (
            <NewOrderView
                businessId={businessId}
                user={user}
                onBack={onNewOrderBack || (() => setShowNewOrder(false))}
                onOrderCreated={() => {
                    setShowNewOrder(false);
                    setSuccessMsg('Order placed successfully!');
                    loadOrders(1);
                    setTimeout(() => setSuccessMsg(''), 4000);
                }}
            />
        );
    }

    return (
        <Box sx={{ pb: 2 }}>
            {successMsg && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
                    {successMsg}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
                My Orders
            </Typography>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : orders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        No orders yet. Tap + to place your first order.
                    </Typography>
                </Box>
            ) : (
                <>
                    {orders.map(order => (
                        <OrderCard key={order.f_id} order={order} />
                    ))}
                    {hasMore && (
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={() => loadOrders(page + 1, true)}
                            disabled={loadingMore}
                            sx={{ mt: 1 }}
                        >
                            {loadingMore ? <CircularProgress size={18} /> : 'Load more'}
                        </Button>
                    )}
                </>
            )}

            <Fab
                color="primary"
                size="medium"
                onClick={() => setShowNewOrder(true)}
                sx={{
                    position: 'fixed',
                    bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
                    right: 16,
                }}
            >
                <Add />
            </Fab>
        </Box>
    );
};

export default CustomerOrdersPage;
